import { useStore } from "@nanostores/react"
import { useEffect, useMemo } from "react"

import { useCachedAo } from "../ao/ao"
import { getTokenInfo, TokenInfo } from "../ao/token"
import { getTokenDetails } from "../ao/tokendrop"
import { COMMON_TOKEN_CHOICES, DAI, NO_TIMEOUT, stETH, W_AR } from "../settings"
import { $tokenInfoCache } from "../stores/token-info-store"

const PRE_CACHED_TOKENS: Record<string, TokenInfo> = {
  ...COMMON_TOKEN_CHOICES.reduce(
    (acc, token) => {
      acc[token.id] = token
      return acc
    },
    {} as Record<string, TokenInfo>,
  ),
  [W_AR.id]: W_AR,
  "0syT13r0s0tgPmIed95bJnuSqaD29HQNN8D3ElLSrsc": {
    id: "0syT13r0s0tgPmIed95bJnuSqaD29HQNN8D3ElLSrsc",
    denomination: 12,
    ticker: "AO",
    logo: "UkS-mdoiG8hcAClhKK8ch4ZhEzla0mCPDOix9hpdSFE",
    name: "AO",
  },
  "8rbAftv7RaPxFjFk5FGUVAVCSjGQB4JHDcb9P9wCVhQ": {
    id: "8rbAftv7RaPxFjFk5FGUVAVCSjGQB4JHDcb9P9wCVhQ",
    name: "Agent",
    ticker: "AGENT",
    denomination: 18,
    logo: "j4u-gMaGNQhXFuh8jqDJkrvGh58M2CD_uvK6kah2NFY",
  },
  [DAI.id]: DAI,
  [stETH.id]: stETH,
}

export function useTokenInfo(tokenId = "", timeout = NO_TIMEOUT): [TokenInfo | undefined, boolean] {
  const tokenIds = useMemo(() => [tokenId], [tokenId])
  const [tokens, loading] = useTokenInfos(tokenIds, timeout)
  return [tokens?.[0], loading]
}

export function useTokenInfos(
  tokenIds: string[],
  timeout?: number,
): [TokenInfo[] | undefined, boolean] {
  const aoCached = useCachedAo()
  const cacheMap = useStore($tokenInfoCache, {
    keys: tokenIds,
  })

  const tokenInfos: TokenInfo[] = useMemo(() => {
    return tokenIds
      .map((tokenId) => {
        if (tokenId in PRE_CACHED_TOKENS) {
          return PRE_CACHED_TOKENS[tokenId]
        }
        const cachedValue = cacheMap ? cacheMap[tokenId] : undefined
        const isFetched = cachedValue !== undefined
        const isValid = cachedValue !== "loading" && cachedValue !== "error"
        return isFetched && isValid ? JSON.parse(cachedValue) : undefined
      })
      .filter((info) => info !== undefined) as TokenInfo[]
  }, [cacheMap, tokenIds])

  const isLoading = useMemo(() => {
    return tokenIds.some((tokenId) => {
      const cachedValue = cacheMap ? cacheMap[tokenId] : undefined
      return cachedValue === "loading"
    })
  }, [cacheMap, tokenIds])

  // TODO
  // const isLoading = useMemo(() => {
  //   return tokenIds.length !== tokenInfos.length
  // }, [cacheMap, tokenIds])

  useEffect(() => {
    const cacheMap = $tokenInfoCache.get()
    tokenIds.forEach((tokenId) => {
      const cachedValue = cacheMap ? cacheMap[tokenId] : undefined
      const isFetched = cachedValue !== undefined
      const isLoading = cachedValue === "loading"

      if (isFetched || isLoading || tokenId in PRE_CACHED_TOKENS || !tokenId) return

      $tokenInfoCache.setKey(tokenId, "loading")

      Promise.all([
        getTokenDetails(tokenId, aoCached, timeout).catch(() => ({ Logo: undefined })),
        getTokenInfo(tokenId, aoCached, timeout),
      ])

        .then(([details, info]) => {
          if (details.Logo) {
            info.logo = details.Logo
          }
          $tokenInfoCache.setKey(tokenId, JSON.stringify(info))
        })
        .catch((error) => {
          console.error(error)
          $tokenInfoCache.setKey(tokenId, "error")
        })
    })
  }, [tokenIds, aoCached, timeout])

  return [tokenInfos, isLoading]
}
